/**
 * v3.20-1508 · Partner Resource Center — final 6-tab build.
 *
 * Final navigation (per user directive — do NOT re-add Compensation
 * or Communications; those features already exist elsewhere in the
 * Hub and must NOT be duplicated):
 *   1. Overview
 *   2. Company
 *   3. Documents
 *   4. Library
 *   5. Money       ← EMBEDS the Money Manager (no launcher)
 *   6. Intake Aliases
 *
 * Tenant isolation: every write is company-scoped by the backend;
 * the server derives company_id from the caller's JWT
 * (see backend/routes/partner_company.py `_pick_target_company`
 *  and backend/routes/partner_money_manager.py `_pick_company`).
 * The browser can never spoof a company_id.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Building2, FileText, Library, Wallet,
  ClipboardList, Sparkles, Search as SearchIcon, Save, Loader2,
  Plus, ShoppingCart, Play, BookOpen, AlertTriangle, CheckCircle2,
  Eye, Download, X, Megaphone,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { InlineViewer } from './PartnerContentManager2Parts';

const humanKind = (t) => ({
  announcement: 'Announcement', training: 'Training', video: 'Video',
  document: 'Document', faq: 'FAQ', marketing: 'Marketing',
  brand: 'Brand', compliance: 'Compliance', support: 'Support',
  funding_disclosure: 'Funding Disclosure',
}[t] || t);

export default function PartnerResourceCenterPage() {
  const { user } = useAuth();
  const isAdmin = ['ptc_admin', 'ptc_director'].includes(user?.role);

  return (
    <div className="space-y-4 p-6" data-testid="partner-resource-center-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="prc-title">
          <Building2 className="h-6 w-6 text-emerald-700" /> Partner Resource Center
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Your company&apos;s self-service business center. Every tab is scoped to your company on the Hub session.
        </p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 max-w-5xl">
          <TabsTrigger value="overview"    data-testid="prc-tab-overview"><Sparkles className="h-3.5 w-3.5 mr-1" />Overview</TabsTrigger>
          <TabsTrigger value="company"     data-testid="prc-tab-company"><Building2 className="h-3.5 w-3.5 mr-1" />Company</TabsTrigger>
          <TabsTrigger value="documents"   data-testid="prc-tab-documents"><FileText className="h-3.5 w-3.5 mr-1" />Documents</TabsTrigger>
          <TabsTrigger value="library"     data-testid="prc-tab-library"><Library className="h-3.5 w-3.5 mr-1" />Library</TabsTrigger>
          <TabsTrigger value="money"       data-testid="prc-tab-money"><Wallet className="h-3.5 w-3.5 mr-1" />Money</TabsTrigger>
          <TabsTrigger value="aliases"     data-testid="prc-tab-aliases"><ClipboardList className="h-3.5 w-3.5 mr-1" />Intake Aliases</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"  className="mt-4"><OverviewTab /></TabsContent>
        <TabsContent value="company"   className="mt-4"><CompanyInfoTab /></TabsContent>
        <TabsContent value="documents" className="mt-4"><DocumentCenterTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="library"   className="mt-4"><ResourceLibraryTab isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="money"     className="mt-4"><MoneyTab /></TabsContent>
        <TabsContent value="aliases"   className="mt-4"><IntakeAliasesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ------------------------------------------------------------------ OVERVIEW
// v3.21-1604 · Partner Command Center — SSOT-wired, single-fold overview.
//   Section 1: Required Actions (unacked required documents — max 3)
//   Section 2: Recent Publications (max 5)
//   Section 3: Announcements (max 3, dismissable)
// No KPI card duplication with Library / Documents.  Preview / Download /
// Acknowledge all round-trip through /content-library/* (audited).
function OverviewTab() {
  const [required, setRequired]     = useState([]);
  const [recent, setRecent]         = useState([]);
  const [announcements, setAnns]    = useState([]);
  const [dismissed, setDismissed]   = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('prc.overview.dismissed') || '[]')); }
    catch { return new Set(); }
  });
  const [busyId, setBusyId]         = useState(null);
  const [previewOf, setPreviewOf]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [flash, setFlash]           = useState(null); // id of newly-acked resource, for the toast

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rq, dash, anns] = await Promise.all([
        api.get('/content-library/required-outstanding').catch(() => ({ data: { items: [] } })),
        api.get('/content-library/dashboard').catch(() => ({ data: { recently_updated: [] } })),
        api.get('/content-library/announcements').catch(() => ({ data: { items: [] } })),
      ]);
      setRequired((rq.data?.items || []).slice(0, 3));
      setRecent((dash.data?.recently_updated || []).slice(0, 5));
      setAnns((anns.data?.items || []).slice(0, 6));
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dismissAnn = (id) => {
    const next = new Set(dismissed); next.add(id); setDismissed(next);
    try { localStorage.setItem('prc.overview.dismissed', JSON.stringify([...next])); } catch { /* noop */ }
  };

  const acknowledge = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/content-library/resources/${r.id}/acknowledge`);
      setFlash(r.title);
      setTimeout(() => setFlash(null), 3000);
      toast.success(`Acknowledged: ${r.title}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Acknowledgement failed'); }
    finally { setBusyId(null); }
  };

  const download = async (r) => {
    try {
      const resp = await api.get(`/content-library/resources/${r.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a'); a.href = url;
      a.download = r.current_version?.original_filename || r.title;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const visibleAnns = announcements.filter((a) => !dismissed.has(a.id)).slice(0, 3);

  if (loading) {
    return (
      <div className="py-6 text-sm text-slate-500 flex items-center gap-2" data-testid="ov-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your command center…
      </div>
    );
  }

  const empty = required.length === 0 && recent.length === 0 && visibleAnns.length === 0;

  return (
    <div className="space-y-4" data-testid="prc-overview">
      {flash && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-2 text-sm flex items-center gap-2" data-testid="ov-flash">
          <CheckCircle2 className="h-4 w-4" /> Acknowledged <span className="font-semibold">{flash}</span>.  You&apos;re all set.
        </div>
      )}

      {/* ── Section 1 · Required Actions ────────────────────────────── */}
      {required.length > 0 && (
        <section data-testid="ov-required">
          {required.map((r) => (
            <div key={r.id} className="rounded-lg border-2 border-rose-300 bg-rose-50/70 p-4 flex items-start gap-3 mb-2 last:mb-0" data-testid={`ov-required-${r.id}`}>
              <AlertTriangle className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Action Required</span>
                  <Badge variant="outline" className="text-[10px] py-0 h-4 border-rose-300 text-rose-700">Required</Badge>
                  {r.category && <span className="text-[11px] text-slate-500">{r.category}</span>}
                </div>
                <div className="font-semibold text-slate-900 mt-0.5">{r.title}</div>
                {r.description && <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">{r.description}</p>}
                <div className="text-[11px] text-slate-500 mt-1">
                  Published {r.date_published ? new Date(r.date_published).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : 'recently'}
                  {r.current_version?.original_filename ? <> · {r.current_version.original_filename}</> : null}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-wrap justify-end">
                <Button size="sm" variant="outline" onClick={() => setPreviewOf(r)} data-testid={`ov-read-${r.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Read</Button>
                <Button size="sm" variant="outline" onClick={() => download(r)} data-testid={`ov-download-${r.id}`}><Download className="h-3.5 w-3.5 mr-1" />Download</Button>
                <Button size="sm" onClick={() => acknowledge(r)} disabled={busyId === r.id} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid={`ov-ack-${r.id}`}>
                  {busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Acknowledge</>}
                </Button>
              </div>
            </div>
          ))}
          {required.length >= 3 && (
            <ViewAllTrigger label="View all required documents" testId="ov-viewall-required" />
          )}
        </section>
      )}

      {/* ── Section 2 · Recent Publications ─────────────────────────── */}
      {recent.length > 0 && (
        <section data-testid="ov-recent">
          <div className="flex items-center justify-between mb-1.5 px-1">
            <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Recent Publications</h3>
            <ViewAllTrigger label="View Library" testId="ov-viewall-library" />
          </div>
          <Card>
            <CardContent className="p-0 divide-y divide-slate-100">
              {recent.map((r) => {
                const isNew = r.date_published && (Date.now() - new Date(r.date_published).getTime()) < 7 * 24 * 3600 * 1000;
                return (
                  <div key={r.id} className="px-3 py-2 flex items-center gap-3" data-testid={`ov-recent-${r.id}`}>
                    <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-800 truncate">{r.title}</span>
                        {isNew && <Badge className="bg-emerald-600 text-white text-[9px] py-0 h-4 px-1.5">NEW</Badge>}
                        {r.is_featured && <Badge className="bg-amber-500 text-white text-[9px] py-0 h-4 px-1.5">FEATURED</Badge>}
                        {r.is_required && <Badge className="bg-rose-600 text-white text-[9px] py-0 h-4 px-1.5">REQUIRED</Badge>}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {r.category || humanKind(r.type)}
                        {r.date_published ? <> · {new Date(r.date_published).toLocaleDateString()}</> : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setPreviewOf(r)} data-testid={`ov-recent-preview-${r.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => download(r)} data-testid={`ov-recent-download-${r.id}`}><Download className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </section>
      )}

      {/* ── Section 3 · Announcements ───────────────────────────────── */}
      {visibleAnns.length > 0 && (
        <section data-testid="ov-announcements">
          <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5 px-1">Announcements</h3>
          <div className="space-y-2">
            {visibleAnns.map((a) => (
              <div key={a.id} className="rounded-md border border-slate-200 bg-white px-3 py-2 flex items-start gap-2" data-testid={`ov-ann-${a.id}`}>
                <Megaphone className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{a.title}</div>
                  <p className="text-xs text-slate-600 whitespace-pre-wrap line-clamp-3">{a.announcement_body || a.description}</p>
                  <div className="text-[10px] text-slate-400 mt-0.5">{a.date_published ? new Date(a.date_published).toLocaleString() : ''}</div>
                </div>
                {(a.announcement_dismissable !== false) && (
                  <button onClick={() => dismissAnn(a.id)} className="text-slate-400 hover:text-slate-700" data-testid={`ov-ann-dismiss-${a.id}`} title="Dismiss">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {empty && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500 text-center" data-testid="ov-empty">
          You&apos;re all caught up.  Nothing requires your attention right now.
        </div>
      )}

      {previewOf && <InlineViewer resource={previewOf} onClose={() => setPreviewOf(null)} />}
    </div>
  );
}

function ViewAllTrigger({ label, testId }) {
  // The tabs live in the parent; we ask the parent to switch by dispatching
  // a `click` on the Library trigger.  No state lift required.
  const go = () => {
    const el = document.querySelector('[data-testid="prc-tab-library"]');
    if (el) el.click();
  };
  return (
    <button type="button" onClick={go} className="text-[11px] font-semibold text-emerald-700 hover:underline" data-testid={testId}>
      {label} →
    </button>
  );
}

// ---------------------------------------------------------- COMPANY INFO TAB
// v3.20-1508 · Fields exactly per user directive.  Every input round-trips
// through PATCH /api/partner-company/mine.  `sender_name`, `sender_email`
// are stored inside the `email_sender_settings` dict (backend already
// accepts this shape).  `notification_preferences` is a dict of booleans
// so we render one toggle per key.
const COMPANY_SECTIONS = [
  { key: 'Identity',        fields: ['name','dba','logo_url','website','phone','email'] },
  { key: 'Address',         fields: ['address_line1','address_line2','city','state','postal_code','country'] },
  { key: 'Mailing Address', fields: ['mailing_address_line1','mailing_city','mailing_state','mailing_postal_code'] },
  { key: 'Primary Contact', fields: ['primary_contact_name','primary_contact_email','primary_contact_phone'] },
  { key: 'Billing Contact', fields: ['billing_contact_name','billing_contact_email','billing_contact_phone'] },
  { key: 'Banking / Wire',  fields: ['bank_name','bank_routing_number','bank_account_number','wire_instructions'], adminOnly: true },
  { key: 'Ownership Defaults', fields: ['default_director_id','default_manager_id','default_sales_rep_id','default_presenter_id','default_vo_officer_id','default_application_concierge_id','default_eqf_team_director_id','default_eqf_team_manager_id'], adminOnly: true },
];
const NOTIFICATION_KEYS = [
  { k: 'new_lead',           label: 'New lead intake' },
  { k: 'appointment_booked', label: 'Appointment booked' },
  { k: 'deal_funded',        label: 'Deal funded' },
  { k: 'payout_sent',        label: 'Payout wire sent' },
  { k: 'invoice_paid',       label: 'Invoice paid' },
  { k: 'weekly_digest',      label: 'Weekly performance digest' },
];
function CompanyInfoTab() {
  const [row, setRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try { const r = await api.get('/partner-company/mine'); setRow(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (name, value) => setRow((r) => ({ ...r, [name]: value }));
  const setSender = (name, value) => setRow((r) => ({
    ...r,
    email_sender_settings: { ...(r?.email_sender_settings || {}), [name]: value },
  }));
  const setPref   = (key, value)  => setRow((r) => ({
    ...r,
    notification_preferences: { ...(r?.notification_preferences || {}), [key]: value },
  }));

  const save = async () => {
    if (!row) return;
    setSaving(true);
    try {
      const payload = {};
      COMPANY_SECTIONS.forEach((s) => s.fields.forEach((f) => { if (row[f] !== undefined) payload[f] = row[f]; }));
      payload.email_sender_settings     = row.email_sender_settings     || {};
      payload.notification_preferences  = row.notification_preferences  || {};
      const r = await api.patch('/partner-company/mine', payload);
      setRow(r.data); toast.success('Saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!row) return <Card><CardContent className="py-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading…</CardContent></Card>;

  const sender = row.email_sender_settings || {};
  const prefs  = row.notification_preferences || {};

  return (
    <div className="space-y-3" data-testid="prc-company-info">
      {COMPANY_SECTIONS.map((sec) => (
        <Card key={sec.key}>
          <CardHeader className="pb-2"><CardTitle className="text-base">{sec.key}{sec.adminOnly && <Badge variant="outline" className="ml-2 text-[10px] py-0 h-4">Admin</Badge>}</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sec.fields.map((f) => (
              <div key={f}>
                <Label htmlFor={f} className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{f.replace(/_/g,' ')}</Label>
                <Input id={f} value={row[f] || ''} onChange={(e) => setField(f, e.target.value)} className="mt-1" data-testid={`co-field-${f}`} />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {/* v3.20-1508 — Sender identity used for outbound emails from this company. */}
      <Card data-testid="co-sender-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">Sender Identity</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Sender Name</Label>
            <Input value={sender.sender_name || ''} onChange={(e) => setSender('sender_name', e.target.value)} className="mt-1" data-testid="co-field-sender_name" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Sender Email</Label>
            <Input value={sender.sender_email || ''} onChange={(e) => setSender('sender_email', e.target.value)} className="mt-1" data-testid="co-field-sender_email" />
          </div>
        </CardContent>
      </Card>

      {/* v3.20-1508 — Notification preferences per Company. */}
      <Card data-testid="co-notifications-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">Notification Preferences</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {NOTIFICATION_KEYS.map((n) => (
            <label key={n.k} className="flex items-center gap-2 text-sm py-1" data-testid={`co-pref-${n.k}`}>
              <input
                type="checkbox"
                checked={!!prefs[n.k]}
                onChange={(e) => setPref(n.k, e.target.checked)}
              />
              <span>{n.label}</span>
            </label>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="co-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save Company Profile
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------- MONEY TAB (embed)
// v3.20-1509 · The Money tab IS the Money Manager — no launcher.
// Reads all 4 sections from a single endpoint: `GET /partner-money/mine`.
// Every dollar figure is computed on the backend from the SAME sources
// as the internal Money Manager (partner_financial_syncs,
// partner_invoices, partner_payments, db.clients).
const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtInt   = (n) => Number(n || 0).toLocaleString('en-US');
const fmtPct   = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
const fmtDays  = (n) => (n == null ? '—' : `${Number(n).toFixed(1)} days`);

function MetricTile({ label, value, tone = 'slate', testId }) {
  const toneCls = {
    emerald: 'border-emerald-100 text-emerald-700',
    amber:   'border-amber-100 text-amber-700',
    sky:     'border-sky-100 text-sky-700',
    violet:  'border-violet-100 text-violet-700',
    rose:    'border-rose-100 text-rose-700',
    slate:   'border-slate-200 text-slate-700',
  }[tone] || 'border-slate-200 text-slate-700';
  return (
    <div className={`rounded-2xl bg-white p-3 shadow-sm border-2 ${toneCls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-xl font-extrabold mt-1">{value}</div>
    </div>
  );
}

function MoneyTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/partner-money/mine');
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load money manager');
      setData(null);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Card><CardContent className="py-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading Money Manager…</CardContent></Card>;
  if (!data)   return <Card><CardContent className="py-6 text-sm text-slate-500">No money data available.</CardContent></Card>;

  const s1 = data.financial_summary      || {};
  const s2 = data.partner_financial_mgmt || {};
  const s3 = data.wire_payments          || {};
  const s4 = data.company_metrics        || {};

  return (
    <div className="space-y-4" data-testid="prc-money">
      {/* Section 1 — Financial Summary */}
      <Card data-testid="money-section-1">
        <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-slate-500">Financial Summary</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricTile label="We Owe Partner"      value={fmtMoney(s1.we_owe_partner)}      tone="rose"    testId="ms1-we-owe" />
          <MetricTile label="Partner Owes Us"     value={fmtMoney(s1.partner_owes_us)}     tone="amber"   testId="ms1-owes-us" />
          <MetricTile label="Total Payments Sent" value={fmtMoney(s1.total_payments_sent)} tone="emerald" testId="ms1-payments-sent" />
          <MetricTile label="Invoices Collected"  value={fmtMoney(s1.invoices_collected)}  tone="emerald" testId="ms1-invoices-collected" />
        </CardContent>
      </Card>

      {/* Section 2 — Partner Financial Management */}
      <Card data-testid="money-section-2">
        <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-slate-500">Partner Financial Management</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <MetricTile label="Outstanding Owed"    value={fmtMoney(s2.outstanding_owed)} tone="rose"    testId="ms2-outstanding" />
          <MetricTile label="Ready to Invoice"    value={fmtInt(s2.ready_to_invoice)}   tone="amber"   testId="ms2-ready" />
          <MetricTile label="Invoiced"            value={fmtInt(s2.invoiced)}            tone="sky"     testId="ms2-invoiced" />
          <MetricTile label="Wired"               value={fmtInt(s2.wired)}               tone="violet"  testId="ms2-wired" />
          <MetricTile label="Paid"                value={fmtInt(s2.paid)}                tone="emerald" testId="ms2-paid" />
          <MetricTile label="Settled (SSOT)"      value={fmtInt(s2.settled_ssot)}        tone="emerald" testId="ms2-settled" />
        </CardContent>
      </Card>

      {/* Section 3 — Wire Payments */}
      <Card data-testid="money-section-3">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm uppercase tracking-wider text-slate-500">Wire Payments</CardTitle>
          <RecordPaymentButton companyId={data.company_id} onRecorded={load} />
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Recent Wire Payments</p>
            {(s3.wire_payments || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">No wire payments yet.</p>
            ) : (
              <ul className="divide-y border rounded text-xs max-h-64 overflow-y-auto" data-testid="ms3-wires">
                {s3.wire_payments.map((w) => (
                  <li key={w.id} className="flex items-center justify-between px-3 py-2" data-testid={`ms3-wire-${w.id}`}>
                    <div>
                      <div className="font-semibold text-slate-800">{fmtMoney(w.amount)}</div>
                      <div className="text-[10px] text-slate-500">{w.payment_date || w.created_at || '—'}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] py-0 h-4">{w.status || '—'}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Recent Invoices</p>
            {(s3.invoices || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">No invoices yet.</p>
            ) : (
              <ul className="divide-y border rounded text-xs max-h-64 overflow-y-auto" data-testid="ms3-invoices">
                {s3.invoices.map((i) => (
                  <li key={i.id} className="flex items-center justify-between px-3 py-2" data-testid={`ms3-invoice-${i.id}`}>
                    <div>
                      <div className="font-semibold text-slate-800">{fmtMoney(i.amount)}</div>
                      <div className="text-[10px] text-slate-500">{i.created_at || '—'}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] py-0 h-4">{i.status || '—'}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Section 4 — Company Metrics */}
      <Card data-testid="money-section-4">
        <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-slate-500">Company Metrics</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MetricTile label="Total Clients"        value={fmtInt(s4.total_clients)}                 testId="ms4-clients" />
          <MetricTile label="Total Deal Value"     value={fmtMoney(s4.total_deal_value)}            testId="ms4-deal-value" />
          <MetricTile label="Funded Deals"         value={fmtInt(s4.funded_deals)}                   tone="emerald" testId="ms4-funded" />
          <MetricTile label="Average Deal Size"    value={fmtMoney(s4.average_deal_size)}            testId="ms4-avg-deal" />
          <MetricTile label="In Pipeline"          value={fmtInt(s4.in_pipeline)}                    tone="sky"     testId="ms4-pipeline" />
          <MetricTile label="Settled This Month"    value={fmtMoney(s4.funded_this_month)}            tone="emerald" testId="ms4-funded-mtd" />
          <MetricTile label="Approval Rate"        value={fmtPct(s4.approval_rate_pct)}              tone="violet"  testId="ms4-approval-rate" />
          <MetricTile label="Funding Rate"         value={fmtPct(s4.funding_rate_pct)}               tone="violet"  testId="ms4-funding-rate" />
          <MetricTile label="Avg Time to Fund"     value={fmtDays(s4.average_time_to_fund_days)}     testId="ms4-avg-ttf" />
          <MetricTile label="Waiting on ACH"       value={fmtInt(s4.waiting_on_ach)}                 tone="amber"   testId="ms4-waiting-ach" />
        </CardContent>
      </Card>
    </div>
  );
}

function RecordPaymentButton({ companyId, onRecorded }) {
  const [open, setOpen]     = useState(false);
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e && e.preventDefault();
    if (!amount) return toast.error('Enter an amount');
    setSaving(true);
    try {
      // Reuse the SAME endpoint used by the master Money Manager.
      await api.post(`/companies/${companyId}/payments`, {
        amount: Number(amount),
        payment_method: 'wire',
        status: 'completed',
        payment_date: new Date().toISOString().slice(0,10),
        notes: 'Recorded via Partner Resource Center',
      });
      toast.success(`Recorded wire of ${fmtMoney(amount)}`);
      setOpen(false); setAmount('');
      onRecorded && onRecorded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Record failed');
    } finally { setSaving(false); }
  };
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} className="bg-emerald-700 hover:bg-emerald-800" data-testid="ms3-record-payment-btn">
        Record Payment
      </Button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-[1100] flex items-center justify-center" onClick={() => setOpen(false)} data-testid="ms3-record-modal-backdrop">
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-5 w-[420px] max-w-[95vw] space-y-3" data-testid="ms3-record-modal">
            <h3 className="text-base font-semibold">Record Wire Payment</h3>
            <div>
              <Label className="text-xs">Amount (USD)</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="ms3-record-amount" autoFocus />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="ms3-record-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

// --------------------------------------------------------- DOCUMENT CENTER
const DOC_CATEGORIES = ['finance','transfer','compliance','legal','hr','marketing','operations'];
function DocumentCenterTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [orderingId, setOrderingId] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get('/partner-resources', { params: { type: 'document', category: category || undefined, q: q || undefined } });
      setItems(r.data?.items || []);
    } catch { setItems([]); }
  }, [category, q]);
  useEffect(() => { load(); }, [load]);
  const order = async (item) => {
    setOrderingId(item.id);
    try {
      await api.post('/document-orders', { resource_id: item.id, quantity: 1 });
      toast.success(`Ordered ${item.title}. A work order was created.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Order failed'); }
    finally { setOrderingId(null); }
  };
  return (
    <div className="space-y-3" data-testid="prc-documents">
      <Card>
        <CardContent className="py-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Search documents" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} className="pl-8 h-9" data-testid="doc-search" />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 border rounded-md text-sm px-2" data-testid="doc-category">
            <option value="">All categories</option>
            {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button size="sm" onClick={load}>Search</Button>
          {isAdmin && <Button size="sm" variant="outline" onClick={() => setShowAdmin((s) => !s)} data-testid="doc-admin-toggle"><Plus className="h-3.5 w-3.5 mr-1" />{showAdmin ? 'Close Admin' : 'Admin: New'}</Button>}
        </CardContent>
      </Card>
      {isAdmin && showAdmin && <AdminResourceForm type="document" onSaved={load} />}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.length === 0 && <Card><CardContent className="py-6 text-sm text-slate-500 italic">No documents yet.</CardContent></Card>}
        {items.map((it) => (
          <Card key={it.id} data-testid={`doc-${it.id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between gap-2">
                <span className="truncate">{it.title}</span>
                {it.category && <Badge variant="outline" className="text-[10px] py-0 h-4 shrink-0">{it.category}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              <p className="text-xs text-slate-600 line-clamp-3">{it.description || 'No description.'}</p>
              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-xs font-semibold text-emerald-700">{it.price != null ? `$${Number(it.price).toFixed(2)}` : 'Included'}</span>
                <Button size="sm" onClick={() => order(it)} disabled={orderingId === it.id} data-testid={`doc-order-${it.id}`}>
                  {orderingId === it.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><ShoppingCart className="h-3 w-3 mr-1" />Order</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <MyOrdersList />
    </div>
  );
}

function MyOrdersList() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { (async () => { try { const r = await api.get('/document-orders/mine'); setOrders(r.data?.items || []); } catch { setOrders([]); } })(); }, []);
  if (!orders.length) return null;
  return (
    <Card data-testid="my-orders">
      <CardHeader className="pb-2"><CardTitle className="text-sm">My Recent Orders ({orders.length})</CardTitle></CardHeader>
      <CardContent className="text-xs space-y-1">
        {orders.slice(0, 10).map((o) => (
          <div key={o.id} className="flex items-center justify-between border-b pb-1 last:border-b-0" data-testid={`order-${o.id}`}>
            <span className="font-medium">{o.resource_title}</span>
            <Badge variant="outline" className="text-[10px] py-0 h-4">{o.status}</Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------- RESOURCE LIBRARY
// v3.21-1604 · Reads from BOTH legacy `/partner-resources` AND the new CMS
// `/content-library/resources`, so the Library reflects everything the
// admin has published without any manual sync.  Sort order (per user
// directive): Featured → Required → New (last 7 days) → Everything else.
function ResourceLibraryTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [previewOf, setPreviewOf] = useState(null);
  const load = useCallback(async () => {
    try {
      const [legacy, cms] = await Promise.all([
        api.get('/partner-resources', { params: { type: type || undefined, q: q || undefined, limit: 100 } }).catch(() => ({ data: { items: [] } })),
        api.get('/content-library/resources', { params: { type: type || undefined, q: q || undefined } }).catch(() => ({ data: { items: [] } })),
      ]);
      const legacyRows = (legacy.data?.items || []).filter((x) => x.type !== 'document').map((x) => ({ ...x, _source: 'legacy' }));
      const cmsRows    = (cms.data?.items    || []).map((x) => ({ ...x, _source: 'cms' }));
      const merged = [...cmsRows, ...legacyRows];
      const SEVEN_D = 7 * 24 * 3600 * 1000;
      const now = Date.now();
      const bucket = (x) => {
        if (x.is_featured) return 0;
        if (x.is_required) return 1;
        const ts = x.date_published ? new Date(x.date_published).getTime() : 0;
        if (ts && (now - ts) < SEVEN_D) return 2;
        return 3;
      };
      merged.sort((a, b) => {
        const d = bucket(a) - bucket(b);
        if (d !== 0) return d;
        // Within same bucket, newest first
        return new Date(b.date_published || b.last_updated || 0).getTime() - new Date(a.date_published || a.last_updated || 0).getTime();
      });
      setItems(merged);
    } catch { setItems([]); }
  }, [type, q]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="prc-library">
      <Card>
        <CardContent className="py-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Search resources" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} className="pl-8 h-9" data-testid="lib-search" />
          </div>
          <select value={type} onChange={(e) => setType(e.target.value)} className="h-9 border rounded-md text-sm px-2" data-testid="lib-type">
            <option value="">All types</option>
            {['announcement','announcements','training','training_documents','video','videos','faq','faqs','marketing','marketing_materials','brand','compliance','compliance_documents','support','support_documents','funding_disclosure','client_disclosures','sales_documents','finance_documents','funding_documents','contracts','scripts','company_forms','other'].map((t) => <option key={t} value={t}>{humanKind(t)}</option>)}
          </select>
          <Button size="sm" onClick={load}>Search</Button>
          {isAdmin && <Button size="sm" variant="outline" onClick={() => setShowAdmin((s) => !s)} data-testid="lib-admin-toggle"><Plus className="h-3.5 w-3.5 mr-1" />{showAdmin ? 'Close Admin' : 'Admin: New'}</Button>}
        </CardContent>
      </Card>
      {isAdmin && showAdmin && <AdminResourceForm type="announcement" onSaved={load} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.length === 0 && <Card><CardContent className="py-6 text-sm text-slate-500 italic">No resources yet.</CardContent></Card>}
        {items.map((it) => (
          <Card key={`${it._source || 'legacy'}-${it.id}`} data-testid={`lib-${it.id}`} className={it.is_featured ? 'border-amber-300 bg-amber-50/30' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between gap-2">
                <span className="truncate flex items-center gap-2">
                  {it.is_featured && <span title="Featured" className="text-amber-500">★</span>}
                  {it.is_required && <span title="Required" className="text-rose-600">⚑</span>}
                  {it.title}
                </span>
                <Badge variant="outline" className="text-[10px] py-0 h-4 shrink-0">{humanKind(it.type)}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-1.5">
              <p className="text-xs text-slate-600 whitespace-pre-wrap line-clamp-4">{it.body || it.description || it.announcement_body}</p>
              {it._source === 'cms' && it.current_version && (
                <div className="flex items-center gap-3 text-xs pt-1 flex-wrap">
                  <span className="text-slate-500">v{it.current_version.version} · {it.current_version.original_filename}</span>
                  <button
                    type="button"
                    onClick={() => setPreviewOf(it)}
                    className="text-emerald-700 hover:underline inline-flex items-center gap-1"
                    data-testid={`lib-preview-${it.id}`}
                  ><Eye className="h-3 w-3" /> Preview</button>
                  <a
                    href="#download"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const r = await api.get(`/content-library/resources/${it.id}/download`, { responseType: 'blob' });
                        const url = URL.createObjectURL(r.data);
                        const a = document.createElement('a'); a.href = url; a.download = it.current_version.original_filename || it.title; a.click();
                        URL.revokeObjectURL(url);
                      } catch { toast.error('Download failed'); }
                    }}
                    className="text-emerald-700 hover:underline inline-flex items-center gap-1"
                    data-testid={`lib-download-${it.id}`}
                  ><Download className="h-3 w-3" /> Download</a>
                </div>
              )}
              {it.media_url && (
                <a href={it.media_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline">
                  {it.type === 'video' ? <Play className="h-3 w-3" /> : <BookOpen className="h-3 w-3" />} Open
                </a>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      {previewOf && <InlineViewer resource={previewOf} onClose={() => setPreviewOf(null)} />}
    </div>
  );
}

// -------------------------------------------------------- ADMIN RESOURCE FORM
function AdminResourceForm({ type: initialType, onSaved }) {
  const [form, setForm] = useState({ type: initialType, category: '', title: '', description: '', body: '', media_url: '', price: '', tags: '', visibility: 'global', active: true });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title) return toast.error('Title required');
    setSaving(true);
    try {
      const payload = { ...form, price: form.price ? Number(form.price) : null, tags: (form.tags || '').split(',').map((s) => s.trim()).filter(Boolean) };
      await api.post('/admin/partner-resources', payload);
      toast.success('Resource published');
      setForm({ ...form, title: '', description: '', body: '', media_url: '', price: '', tags: '' });
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <Card data-testid="admin-resource-form" className="border-emerald-300 bg-emerald-50/30">
      <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-900">Admin — Publish resource</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Type</Label>
          <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="w-full h-9 border rounded-md text-sm px-2 mt-1" data-testid="adm-type">
            {['announcement','training','video','document','faq','marketing','brand','compliance','support','funding_disclosure'].map((t) => <option key={t} value={t}>{humanKind(t)}</option>)}
          </select>
        </div>
        {form.type === 'document' && (
          <div>
            <Label className="text-xs">Category</Label>
            <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="w-full h-9 border rounded-md text-sm px-2 mt-1" data-testid="adm-category">
              <option value="">—</option>
              {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
        <div className="md:col-span-2"><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="adm-title" /></div>
        <div className="md:col-span-2"><Label className="text-xs">Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="adm-desc" /></div>
        <div className="md:col-span-2"><Label className="text-xs">Body / Content</Label><Textarea rows={4} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} data-testid="adm-body" /></div>
        <div><Label className="text-xs">Media URL</Label><Input value={form.media_url} onChange={(e) => setForm({...form, media_url: e.target.value})} data-testid="adm-media" /></div>
        <div><Label className="text-xs">Price</Label><Input type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} data-testid="adm-price" /></div>
        <div><Label className="text-xs">Tags (comma sep)</Label><Input value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} data-testid="adm-tags" /></div>
        <div><Label className="text-xs">Visibility (global or company_id)</Label><Input value={form.visibility} onChange={(e) => setForm({...form, visibility: e.target.value})} data-testid="adm-vis" /></div>
        <div className="md:col-span-2 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="adm-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Publish'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------ INTAKE ALIASES TAB
function IntakeAliasesTab() {
  // v3.20-1507 · Wire the Partner Resource Center's Intake Alias
  // Manager to the SSOT `db.company_aliases` collection via the
  // self-service `/api/company-aliases/mine` endpoints.  Every add /
  // remove takes effect on the VERY NEXT intake — no cache, no
  // restart.  Case-only variants (FSG / fsg / Fsg) collapse onto a
  // single normalized row per company.
  const [items, setItems]         = useState([]);
  const [canonical, setCanonical] = useState('');
  const [newAlias, setNewAlias]   = useState('');
  const [loading, setLoading]     = useState(true);
  const [adding, setAdding]       = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/company-aliases/mine');
      setItems(data?.items || []);
      setCanonical(data?.canonical_name || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load aliases');
      setItems([]);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addAlias = async () => {
    const value = (newAlias || '').trim();
    if (!value) { toast.error('Enter an alias first'); return; }
    setAdding(true);
    try {
      const { data } = await api.post('/company-aliases/mine', { alias: value });
      toast.success(`Alias '${data.item.alias}' saved`);
      setNewAlias('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    } finally { setAdding(false); }
  };

  const removeAlias = async (row) => {
    if (!window.confirm(`Remove alias '${row.alias}'?`)) return;
    setRemovingId(row.id);
    try {
      await api.delete(`/company-aliases/mine/${row.id}`);
      toast.success(`Alias '${row.alias}' removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    } finally { setRemovingId(null); }
  };

  return (
    <Card data-testid="prc-aliases">
      <CardHeader>
        <CardTitle className="text-base">Intake Aliases</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-600">
          Add any spelling, abbreviation, DBA, or variation incoming leads may contain — every rep-company string listed here will route new intakes to{' '}
          <span className="font-semibold text-slate-800">{canonical || 'your company'}</span>.  Examples:{' '}
          <code className="bg-slate-100 px-1 rounded">ABC</code>,{' '}
          <code className="bg-slate-100 px-1 rounded">ABC Company</code>,{' '}
          <code className="bg-slate-100 px-1 rounded">ABC Co.</code>,{' '}
          <code className="bg-slate-100 px-1 rounded">ABC Services</code>.  Changes take effect on the very next intake — no restart needed.
        </p>

        <div className="flex items-end gap-2 max-w-xl">
          <div className="flex-1">
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              New alias
            </label>
            <Input
              data-testid="prc-alias-input"
              value={newAlias}
              onChange={(e) => setNewAlias(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addAlias(); }}
              placeholder="e.g. ABC or ABC Company"
              maxLength={120}
            />
          </div>
          <Button
            data-testid="prc-alias-add"
            onClick={addAlias}
            disabled={adding || !newAlias.trim()}
            className="bg-slate-800 hover:bg-slate-900 text-white"
          >
            {adding
              ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
              : <Plus className="w-4 h-4 mr-2" />}
            Add
          </Button>
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading aliases…
          </div>
        ) : items.length === 0 ? (
          <div
            className="text-sm text-slate-500 italic"
            data-testid="prc-aliases-empty"
          >
            No aliases yet — add one above so intake auto-assigns your company.
          </div>
        ) : (
          <ul
            className="divide-y divide-slate-100 border border-slate-200 rounded"
            data-testid="prc-aliases-list"
          >
            {items.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between px-3 py-2 gap-3"
                data-testid={`prc-alias-row-${a.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">
                    {a.alias}
                  </code>
                  <span className="text-[10px] text-slate-400 truncate">
                    normalized · {a.alias_normalized}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeAlias(a)}
                  disabled={removingId === a.id}
                  className="text-[11px] font-semibold text-red-600 hover:text-red-800 disabled:opacity-50"
                  data-testid={`prc-alias-remove-${a.id}`}
                >
                  {removingId === a.id ? 'Removing…' : 'Remove'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
